import React from 'react';
import { Stack, TextField, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormDropdownMenuDphCounterController } from '../FormDropdownMenuDphCounterController';
import { formatDecimalValueNumberInput } from '@/core/helpers/form';
import palette from '@/theme/palette.ts';

type PriceWithDphCounterProps = {
  fieldOnBlur?: () => void;
};

export const PriceWithDphCounter = ({ fieldOnBlur }: PriceWithDphCounterProps) => {
  const { t } = useTranslation();
  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext();
  const taxGroup = watch('tax_group');

  const handleTotalPriceChange = (
    newTotal: string,
    onChange: (value: number | undefined) => void,
  ) => {
    const cleanedTotalPrice = formatDecimalValueNumberInput(newTotal);
    const basePrice = Number(cleanedTotalPrice) / (Number(taxGroup.value) / 100 + 1);
    setValue('base_price', basePrice.toFixed(3).toString().replace(/\.0+$/, ''));
    onChange(cleanedTotalPrice === '' ? 0 : Number(cleanedTotalPrice));
  };

  return (
    <Stack
      direction="row"
      mt={1}
      alignItems="center"
      sx={{
        backgroundColor: palette.surface.lighter,
        p: '20px 20px 0 20px',
        borderRadius: '10px',
      }}
    >
      <Controller
        name="base_price"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            type="string"
            disabled
            value={field.value?.toString() || ''}
            label={t('HEADERS.BASE_PRICE')}
            error={!!errors.basePrice}
            helperText={errors.basePrice?.message?.toString() || ' '}
            fullWidth
            InputProps={{
              inputProps: {
                min: 0,
                step: '0.01',
              },
              endAdornment: (
                <Typography variant="body1" color="text.secondary">
                  Kč
                </Typography>
              ),
            }}
          />
        )}
      />
      <span
        className="material-symbols-rounded"
        style={{ color: palette.text.secondary, fontSize: '1.8rem', marginBottom: '20px' }}
      >
        add
      </span>
      <FormDropdownMenuDphCounterController
        controllerName={'tax_id'}
        dropdownOnChange={fieldOnBlur}
        sx={{ mb: 2.5 }}
        preSelectedTaxValue={taxGroup?.value}
      />
      <span
        className="material-symbols-rounded"
        style={{ color: palette.text.secondary, fontSize: '1.5rem', marginBottom: '20px' }}
      >
        equal
      </span>
      <Controller
        name="price"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            value={field.value || 0}
            onChange={(e) => handleTotalPriceChange(e.target.value, field.onChange)}
            onBlur={() => {
              field.onBlur();
              fieldOnBlur?.();
            }}
            label={t('HEADERS.PRICE')}
            helperText={' '}
            InputProps={{
              endAdornment: (
                <Typography variant="body1" color="text.secondary">
                  Kč
                </Typography>
              ),
            }}
            fullWidth
          />
        )}
      />
    </Stack>
  );
};
